import React from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';

import { withLayoutAndSession } from '@utils/hocs';
import { PageWithProvidersAndLayout } from '@utils/types/nextApp';
import { BlogCategoryType, BlogType } from '@utils/types/blog';
import { getBlogsByCategory } from '@api/blog/getBlogsByCategory';
import { getAllCategories } from '@api/blog/getAllCategories';
import { getCategoryBySlug } from '@api/blog/getCategoryBySlug';
import BlogList from '@components/blog/blogList';

import styles from './styles.module.scss';

type Props = {
  blogs: BlogType[];
  categoryInfo: BlogCategoryType;
};

const Category: PageWithProvidersAndLayout<Props> = (props: Props) => {
  const { blogs, categoryInfo } = props;

  return (
    <>
      {categoryInfo.metaTitle && (
        <Head>
          <title>{categoryInfo.metaTitle}</title>
          <meta
            name="description"
            content={categoryInfo.metaDescription}
            key="description"
          />
        </Head>
      )}
      <main className={styles.container}>
        <div className={styles.categoryPage}>
          <BlogList blogs={blogs} containerStyle={styles.latestBlogs} />
        </div>
      </main>
    </>
  );
};

export const getStaticProps: GetStaticProps<any> = async (context) => {
  if (!context.params) {
    throw Error('Slug is required');
  }

  try {
    const slug = context.params.slug as string;
    const blogs = await getBlogsByCategory(slug);
    const categoryInfo = await getCategoryBySlug(slug);

    if (!blogs?.length || !categoryInfo) {
      return {
        notFound: true,
        revalidate: 10,
      };
    }

    return {
      props: {
        blogs,
        categoryInfo,
      },
      revalidate: 30,
    };
  } catch (error) {
    return {
      notFound: true,
      revalidate: 10,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  const categoriesIDs = await getAllCategories();

  return {
    paths: categoriesIDs.map(({ slug: { current: slug } }) => {
      return {
        params: {
          slug,
        },
      };
    }),
    fallback: 'blocking',
  };
};

export default withLayoutAndSession(Category, {
  headerProps: { theme: 'light' },
  footerProps: { theme: 'light' },
});
